import each from 'lodash.foreach';
import orderBy from 'lodash.orderby';
import get from 'lodash.get';
import moment from 'moment';
import { isEmpty, isEmptyObject } from '../../core/util';

export const sortFundList = (allFunds, sortProperty, isReverse) => {
  const sorted = {};
  each(allFunds, (group, groupName) => {
    sorted[groupName] = orderBy(
      group,
      fund => get(fund, sortProperty),
      isReverse ? 'desc' : 'asc'
    );
  });
  return sorted;
};

export const endOfQuarter = () => {
  return moment()
    .subtract(1, 'quarter')
    .endOf('quarter')
    .format('MM/DD/YYYY');
};

export const endOfMonth = () => {
  return moment()
    .subtract(1, 'month')
    .endOf('month')
    .format('MM/DD/YYYY');
};

export const fundYieldDates = groups => {
  const results = {};

  if (isEmptyObject(groups) || isEmpty(groups)) return results;

  each(groups?.[('Tax-Free Money Market', 'Taxable Money Market')], fund => {
    if (!results.simpleDate && fund.sevenDayYieldSimpleAsOfDate) {
      results.simpleDate = moment
        .parseZone(fund.sevenDayYieldSimpleAsOfDate)
        .format('MM/DD/YYYY');
    }
    if (
      !results.compoundDate &&
      fund.unsubsidizedSevenDayYieldCompoundAsOfDate
    ) {
      results.compoundDate = moment
        .parseZone(fund.unsubsidizedSevenDayYieldCompoundAsOfDate)
        .format('MM/DD/YYYY');
    }
  });
  // find first fund with asOfDate for quarterly average annual total returns
  // then formate it for display
  const fundWithDate = groups?.['Blended Asset']?.find(fund => {
    return fund?.standardPerformanceReturns?.quarterlyAnnualReturns
      ?.fundPerformance?.asOfDate;
  });
  results.averageAnnualQuarterlyDate = moment
    .parseZone(
      fundWithDate?.standardPerformanceReturns?.quarterlyAnnualReturns
        ?.fundPerformance?.asOfDate
    )
    .format('MM/DD/YYYY');
  return results;
};

export const isFundClosed = fund => {
  return !fund.openToNewInvestors;
};

export const fundListConfig = {
  columns: {
    select: {
      displayName: '',
      disableSorting: true
    },
    name: {
      id: 'fundName',
      displayName: 'Fund Name',
      sortProperty: 'name'
    },
    morningStar: {
      id: 'morningStarOverallRating',
      displayName: `Overall Morningstar Rating as of ${endOfQuarter()}`,
      sortProperty: 'morningStarOverallRating'
    },
    subAssetClassName: {
      id: 'morningStarCategoryName',
      displayName: 'Morningstar Category',
      sortProperty: 'morningStarCategoryName'
    },
    fundRiskReward: {
      id: 'riskLevel',
      displayName: 'Risk Potential** (Standard Deviation)',
      sortProperty: 'riskLevel'
    },
    oneYearReturn: {
      id: '1YearReturn',
      displayName: '1 Year',
      sortProperty: 'monthlyAverageAnnualTotalReturns.oneYearReturn',
      isAvgReport: true,
      isNumber: true,
      reportType: 'average'
    },
    fiveYearReturn: {
      id: '5YearReturn',
      displayName: '5 Years',
      sortProperty: 'monthlyAverageAnnualTotalReturns.fiveYearReturn',
      isAvgReport: true,
      isNumber: true,
      reportType: 'average'
    },
    tenYearReturn: {
      id: '10YearReturn',
      displayName: '10 Years',
      sortProperty: 'monthlyAverageAnnualTotalReturns.tenYearReturn',
      isAvgReport: true,
      isNumber: true,
      reportType: 'average'
    },
    sinceInceptionReturn: {
      id: 'sinceInceptionReturn',
      displayName: 'Since Inception',
      sortProperty: 'monthlyAverageAnnualTotalReturns.sinceInceptionReturn',
      isAvgReport: true,
      isNumber: true,
      reportType: 'average'
    },
    sevenDayYieldSimple: {
      id: 'sevenDayYieldSimple',
      displayName: 'with waiver',
      sortProperty: 'sevenDayYieldSimple',
      isAvgReport: true,
      isNumber: true,
      reportType: 'yield'
    },
    unsubsidizedSevenDayYieldSimple: {
      id: 'unsubsidizedSevenDayYieldSimple',
      displayName: 'w/out waiver',
      sortProperty: 'unsubsidizedSevenDayYieldSimple',
      isAvgReport: true,
      isNumber: true,
      reportType: 'yield'
    },
    sevenDayAnnualizedDividendYieldCompound: {
      id: 'sevenDayAnnualizedDividendYieldCompound',
      displayName: '',
      sortProperty: 'sevenDayAnnualizedDividendYieldCompound',
      isAvgReport: true,
      isNumber: true,
      reportType: 'yield'
    },
    unsubsidizedSevenDayYieldCompound: {
      id: 'unsubsidizedSevenDayYieldCompound',
      displayName: '',
      sortProperty: 'unsubsidizedSevenDayYieldCompound',
      isAvgReport: true,
      isNumber: true,
      reportType: 'yield'
    },
    expenseRatio: {
      id: 'grossExpenseRatio',
      displayName: 'Gross Expense Ratio',
      sortProperty: 'grossExpenseRatio',
      isNumber: true
    },
    add: {
      displayName: '',
      disableSorting: true
    }
  }
};

export const getColumnWidth = column => {
  if (column?.sortProperty === fundListConfig.columns.name.sortProperty) {
    return 156;
  }
  if (column?.disableSorting) {
    return 0;
  }
  return 105;
};

export const mockFund = [
  {
    name: 'Financial Services',
    shortName: 'Financial Services',
    inceptionDate: 844056000000,
    twelveMonthLowPrice: {
      price: 45.0
    },
    ticker: 'PRISX',
    twelveMonthHighPrice: {
      price: 90.0
    },
    investmentVehicleKey: {
      cusip: '77954N103',
      ticker: 'PRISX',
      recordKeepingCode: '117',
      trpcode: 'FSF'
    },
    mutualFundManagers: [
      {
        managerName: 'John Smith',
        managedFundSinceDate: '2015-1-1',
        joinedFirmDate: '2015-1-1'
      }
    ],
    price: {
      asOfDate: 1580101200000,
      price: 27.85,
      priceChange: -0.33,
      pricePercentChange: -1.17,
      dailyAccrualRate: 0,
      refreshDate: 1580171526000
    },
    assetAllocationGroupedRankings: [
      {
        rank: 0,
        name: 'Foreign Bonds',
        percentTotalNetAssets: 25,
        asOfDate: '2020-01-31T05:00:00Z',
        __typename: 'Ranking'
      },
      {
        rank: 0,
        name: 'Preferred Stock',
        percentTotalNetAssets: 0,
        asOfDate: '2020-01-31T05:00:00Z',
        __typename: 'Ranking'
      },
      {
        rank: 0,
        name: 'Convertibles',
        percentTotalNetAssets: 50,
        asOfDate: '2020-01-31T05:00:00Z',
        __typename: 'Ranking'
      },
      {
        rank: 0,
        name: 'Cash',
        percentTotalNetAssets: -0.2,
        asOfDate: '2020-01-31T05:00:00Z',
        __typename: 'Ranking'
      }
    ],
    __typename: 'GraphQLMutualFund'
  },
  {
    name: 'Africa & Middle East Fund',
    ticker: 'PRFRX',
    assetAllocationGroupedRankings: [
      {
        rank: 0,
        name: 'Foreign Bonds',
        percentTotalNetAssets: 25,
        asOfDate: '2020-01-31T05:00:00Z',
        __typename: 'Ranking'
      },
      {
        rank: 0,
        name: 'Preferred Stock',
        percentTotalNetAssets: 0,
        asOfDate: '2020-01-31T05:00:00Z',
        __typename: 'Ranking'
      },
      {
        rank: 0,
        name: 'Convertibles',
        percentTotalNetAssets: 50,
        asOfDate: '2020-01-31T05:00:00Z',
        __typename: 'Ranking'
      },
      {
        rank: 0,
        name: 'Cash',
        percentTotalNetAssets: -0.2,
        asOfDate: '2020-01-31T05:00:00Z',
        __typename: 'Ranking'
      }
    ],
    stockExchange: null,
    stockSymbolCode: null,
    expenseRatioAsOfDate: null,
    iClass: false,
    rClass: false,
    advisorClass: false,
    assetClassName: 'Domestic Stock',
    assetClassID: 100,
    assetClassParentID: 10,
    trpFund: true,
    moneyMarket: false,
    openToNewInvestors: true,
    openToSubsequentInvestments: true,
    bond: false,
    variableAnnuity: false,
    variableAnnuityTrust: false,
    readPrice: true,
    moneyMarketType: null,
    expenseRatio: 0.87,
    fundDescription:
      "Seeks to provide long-term growth of capital and a modest level of income by investing at least 80% of its net assets in common stocks of financial services companies. The fund may also invest in companies that derive substantial revenues from conducting business with the financial services industry. Although the fund's narrow investment focus makes it potentially riskier than a more broadly diversified fund, dividends paid by financial services companies may help to moderate this risk to some extent. The fund is intended for long-term investors who can accept the price fluctuations inherent in common stock investing. All investments are subject to risk including the possible loss of principal.",
    taxFree: false,
    minimumInvestmentAmount: 2500,
    specificState: null,
    availableStates: [
      'AA',
      'AE',
      'AK',
      'AL',
      'AP',
      'AR',
      'AS',
      'AZ',
      'CA',
      'CO',
      'CT',
      'DC',
      'DE',
      'FL',
      'FM',
      'GA',
      'GU'
    ],
    redemptionFee: 0,
    objective:
      'The fund seeks long-term growth of capital and a modest level of income.',
    state: null,
    targetYear: 0,
    spectrum: false,
    summit: false,
    retirement: false,
    subAssetCategoryID: 0,
    subAssetClassName: null,
    managementFee: 0.64,
    fee121b: 0.64,
    accountMaintenanceFee: 0,
    redemptionFeePeriod: 'N/A',
    mortalityExpense: 0,
    fiscalQuarterEndExpenseRatio: 0.87,
    fiscalQuarterEndDate: 1577768400000,
    institutional: false,
    advisory: false,
    fiscalYearEnd: null,
    fiscalYearEndMonth: null,
    wsjAbbreviation: null,
    strategyText: null,
    riskRewardText: null,
    fundRiskReward: {
      riskLevelText: 'Higher',
      riskLevel: 270
    },
    investorProfileText: null,
    monthlyComponentsAsOfDate: null,
    transactionFee: false,
    shortTermTradingFee: 0,
    shortTermTradingFeePeriod: null,
    frontEndLoad: 0,
    backEndLoad: 0,
    loadHistoryText: null,
    accountMinimumAddToAccount: 100,
    accountMinimumOpenIRA: 1000,
    accountMinimumAddToIRA: 100,
    accountMinimumSystematicPurchase: 0,
    grossExpenseRatio: 0.86,
    netExpenseRatio: 0.87,
    expenseRatioWaiverType: null,
    expenseRatioWaiverExpirationDate: null,
    morningStarRatings: {
      asOfDate: 1569816000000,
      morningStarCategoryName: 'Financial',
      morningStarOverallRating: 4,
      morningStarOverallQuantity: 91
    },
    moneyMarketReformMessages: null,
    fundExpense: 0,
    assetBasedFee: 0,
    portfolioInfoAsOfDate: null,
    retirementDateFund: false,
    targetDateFund: false,
    twelveMonthLowPrice: {
      asOfDate: 1467000000000,
      price: 65.2,
      priceChange: 0,
      pricePercentChange: 0,
      dailyAccrualRate: 0,
      refreshDate: null
    },
    twelveMonthHighPrice: {
      asOfDate: 1494820800000,
      price: 84.73,
      priceChange: 0,
      pricePercentChange: 0,
      dailyAccrualRate: 0,
      refreshDate: null
    }
  },
  {
    name: 'Financial Services lkasdjsj',
    shortName: 'Financial Services sdflgjk;fg',
    inceptionDate: 844056000000,
    investmentVehicleKey: {
      cusip: '77954N103',
      ticker: 'PRISXffdfg',
      recordKeepingCode: '117',
      trpcode: 'FSF'
    },
    price: {
      asOfDate: 1580101200000,
      price: 27.85,
      priceChange: -0.33,
      pricePercentChange: -1.17,
      dailyAccrualRate: 0,
      refreshDate: 1580171526000
    },
    stockExchange: null,
    stockSymbolCode: null,
    expenseRatioAsOfDate: null,
    iClass: false,
    rClass: false,
    advisorClass: false,
    assetClassName: 'Domestic Stock',
    assetClassID: 100,
    assetClassParentID: 10,
    trpFund: true,
    moneyMarket: false,
    openToNewInvestors: false,
    openToSubsequentInvestments: true,
    bond: false,
    variableAnnuity: false,
    variableAnnuityTrust: false,
    readPrice: true,
    moneyMarketType: null,
    expenseRatio: 0.87,
    fundDescription:
      "Seeks to provide long-term growth of capital and a modest level of income by investing at least 80% of its net assets in common stocks of financial services companies. The fund may also invest in companies that derive substantial revenues from conducting business with the financial services industry. Although the fund's narrow investment focus makes it potentially riskier than a more broadly diversified fund, dividends paid by financial services companies may help to moderate this risk to some extent. The fund is intended for long-term investors who can accept the price fluctuations inherent in common stock investing. All investments are subject to risk including the possible loss of principal.",
    taxFree: false,
    minimumInvestmentAmount: 2500,
    specificState: null,
    availableStates: [
      'AA',
      'AE',
      'AK',
      'AL',
      'AP',
      'AR',
      'AS',
      'AZ',
      'CA',
      'CO',
      'CT',
      'DC',
      'DE',
      'FL',
      'FM',
      'GA',
      'GU'
    ],
    redemptionFee: 0,
    objective:
      'The fund seeks long-term growth of capital and a modest level of income.',
    state: null,
    targetYear: 0,
    spectrum: false,
    summit: false,
    retirement: false,
    subAssetCategoryID: 0,
    subAssetClassName: null,
    managementFee: 0.64,
    fee121b: 0.64,
    accountMaintenanceFee: 0,
    redemptionFeePeriod: 'N/A',
    mortalityExpense: 0,
    fiscalQuarterEndExpenseRatio: 0.87,
    fiscalQuarterEndDate: 1577768400000,
    institutional: false,
    advisory: false,
    fiscalYearEnd: null,
    fiscalYearEndMonth: null,
    wsjAbbreviation: null,
    strategyText: null,
    riskRewardText: null,
    fundRiskReward: {
      riskLevelText: 'Higher',
      riskLevel: 270
    },
    investorProfileText: null,
    monthlyComponentsAsOfDate: null,
    transactionFee: false,
    shortTermTradingFee: 0,
    shortTermTradingFeePeriod: null,
    frontEndLoad: 0,
    backEndLoad: 0,
    loadHistoryText: null,
    accountMinimumAddToAccount: 100,
    accountMinimumOpenIRA: 1000,
    accountMinimumAddToIRA: 100,
    accountMinimumSystematicPurchase: 0,
    grossExpenseRatio: 0.87,
    netExpenseRatio: 0.87,
    expenseRatioWaiverType: null,
    expenseRatioWaiverExpirationDate: null,
    morningStarRatings: {
      asOfDate: 1569816000000,
      morningStarCategoryName: 'crudaton',
      morningStarOverallRating: 4,
      morningStarOverallQuantity: 91
    },
    moneyMarketReformMessages: null,
    fundExpense: 0,
    assetBasedFee: 0,
    portfolioInfoAsOfDate: null,
    retirementDateFund: false,
    targetDateFund: false,
    twelveMonthLowPrice: {
      asOfDate: 1467000000000,
      price: 65.2,
      priceChange: 0,
      pricePercentChange: 0,
      dailyAccrualRate: 0,
      refreshDate: null
    },
    twelveMonthHighPrice: {
      asOfDate: 1494820800000,
      price: 84.73,
      priceChange: 0,
      pricePercentChange: 0,
      dailyAccrualRate: 0,
      refreshDate: null
    }
  },
  {
    name: 'Financial Services 2',
    shortName: 'Financial Services',
    inceptionDate: 844056000000,
    investmentVehicleKey: {
      cusip: '77954N103',
      ticker: 'PRISX',
      recordKeepingCode: '117',
      trpcode: 'FSF'
    },
    price: {
      asOfDate: 1580101200000,
      price: 27.85,
      priceChange: -0.33,
      pricePercentChange: -1.17,
      dailyAccrualRate: 0,
      refreshDate: 1580171526000
    },
    stockExchange: null,
    stockSymbolCode: null,
    expenseRatioAsOfDate: null,
    iClass: false,
    rClass: false,
    advisorClass: false,
    assetClassName: 'Taxable Money Market',
    assetClassID: 100,
    assetClassParentID: 10,
    trpFund: true,
    moneyMarket: false,
    openToNewInvestors: true,
    openToSubsequentInvestments: true,
    bond: false,
    variableAnnuity: false,
    variableAnnuityTrust: false,
    readPrice: true,
    moneyMarketType: null,
    expenseRatio: 0.87,
    fundDescription:
      "Seeks to provide long-term growth of capital and a modest level of income by investing at least 80% of its net assets in common stocks of financial services companies. The fund may also invest in companies that derive substantial revenues from conducting business with the financial services industry. Although the fund's narrow investment focus makes it potentially riskier than a more broadly diversified fund, dividends paid by financial services companies may help to moderate this risk to some extent. The fund is intended for long-term investors who can accept the price fluctuations inherent in common stock investing. All investments are subject to risk including the possible loss of principal.",
    taxFree: false,
    minimumInvestmentAmount: 2500,
    specificState: null,
    availableStates: [
      'AA',
      'AE',
      'AK',
      'AL',
      'AP',
      'AR',
      'AS',
      'AZ',
      'CA',
      'CO',
      'CT',
      'DC',
      'DE',
      'FL',
      'FM',
      'GA',
      'GU'
    ],
    redemptionFee: 0,
    objective:
      'The fund seeks long-term growth of capital and a modest level of income.',
    state: null,
    targetYear: 0,
    spectrum: false,
    summit: false,
    retirement: false,
    subAssetCategoryID: 0,
    subAssetClassName: null,
    managementFee: 0.64,
    fee121b: 0.64,
    accountMaintenanceFee: 0,
    redemptionFeePeriod: 'N/A',
    mortalityExpense: 0,
    fiscalQuarterEndExpenseRatio: 0.87,
    fiscalQuarterEndDate: 1577768400000,
    institutional: false,
    advisory: false,
    fiscalYearEnd: null,
    fiscalYearEndMonth: null,
    wsjAbbreviation: null,
    strategyText: null,
    riskRewardText: null,
    fundRiskReward: {
      riskLevelText: 'Higher',
      riskLevel: 270
    },
    investorProfileText: null,
    monthlyComponentsAsOfDate: null,
    transactionFee: false,
    shortTermTradingFee: 0,
    shortTermTradingFeePeriod: null,
    frontEndLoad: 0,
    backEndLoad: 0,
    loadHistoryText: null,
    accountMinimumAddToAccount: 100,
    accountMinimumOpenIRA: 1000,
    accountMinimumAddToIRA: 100,
    accountMinimumSystematicPurchase: 0,
    grossExpenseRatio: 0.87,
    netExpenseRatio: 0.87,
    expenseRatioWaiverType: null,
    expenseRatioWaiverExpirationDate: null,
    morningStarRatings: {
      asOfDate: 1569816000000,
      morningStarCategoryName: 'Financial',
      morningStarOverallRating: 4,
      morningStarOverallQuantity: 91
    },
    moneyMarketReformMessages: null,
    fundExpense: 0,
    assetBasedFee: 0,
    portfolioInfoAsOfDate: null,
    retirementDateFund: false,
    targetDateFund: false,
    twelveMonthLowPrice: {
      asOfDate: 1467000000000,
      price: 65.2,
      priceChange: 0,
      pricePercentChange: 0,
      dailyAccrualRate: 0,
      refreshDate: null
    },
    twelveMonthHighPrice: {
      asOfDate: 1494820800000,
      price: 84.73,
      priceChange: 0,
      pricePercentChange: 0,
      dailyAccrualRate: 0,
      refreshDate: null
    }
  }
];
